<template>
  <section class="areas section" aria-labelledby="areas-heading">
    <header class="areas-head">
      <h2 id="areas-heading" class="areas-title">Tres áreas, un mismo equipo</h2>
      <p class="areas-intro">
        Elige por dónde empezar. Cada área resuelve un frente distinto de tu
        operación, y todas las trabaja el mismo equipo de ingeniería.
      </p>
    </header>

    <ul class="areas-grid">
      <li v-for="area in areas" :key="area.id" class="area-card">
        <!-- El marco de imagen mantiene proporción fija: sustituir el activo
             por la fotografía profesional no requiere tocar estilos. -->
        <div class="area-media">
          <img :src="area.image" :alt="area.imageAlt" loading="lazy" decoding="async" />
        </div>

        <div class="area-body">
          <h3 class="area-name">{{ area.name }}</h3>
          <p class="area-promise">{{ area.promise }}</p>

          <ul class="area-points">
            <li v-for="point in area.points" :key="point">{{ point }}</li>
          </ul>

          <router-link :to="area.to" class="area-cta">
            <span>{{ area.cta }}</span>
            <ArrowRight :size="18" :stroke-width="3" />
          </router-link>
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
import { ArrowRight } from 'lucide-vue-next';

export default {
  name: 'AreasSection',
  components: { ArrowRight },
  data() {
    return {
      areas: [
        {
          id: 'impresion3d',
          name: 'Impresión 3D',
          promise: 'Del archivo a la pieza real, lista para usar.',
          points: [
            'Modelado de piezas a medida',
            'Prototipos funcionales',
            'Producción bajo demanda',
          ],
          cta: 'Cotiza tu impresión 3D',
          to: '/print3d',
          image: require('../../assets/workingPeople.svg'),
          imageAlt: 'Piezas diseñadas y fabricadas con impresión 3D',
        },
        {
          id: 'automatizacion',
          name: 'Automatización de procesos',
          promise: 'Menos tareas manuales y más control sobre tu operación.',
          points: [
            'Flujos de trabajo automatizados',
            'Seguimiento y trazabilidad',
            'Integración de tus herramientas',
          ],
          cta: 'Automatiza tu operación',
          to: { path: '/services', hash: '#area-automatizacion' },
          image: require('../../assets/SelkMake.svg'),
          imageAlt: 'Automatización de procesos empresariales',
        },
        {
          id: 'aplicativos',
          name: 'Aplicativos web',
          promise: 'El software que ordena tu negocio, hecho a tu medida.',
          points: [
            'Sistemas de gestión a medida',
            'Plataformas para clientes y equipo',
            'Sitios web y e-commerce',
          ],
          cta: 'Conoce el desarrollo web',
          to: { path: '/services', hash: '#area-aplicativos' },
          image: require('../../assets/webPageService.svg'),
          imageAlt: 'Aplicativos web y plataformas a medida',
        },
      ],
    };
  },
};
</script>

<style scoped>
.areas-head {
  max-width: 46rem;
  margin-bottom: var(--space-lg);
}

.areas-title {
  font-family: var(--font-subtitle);
  font-weight: 700;
  font-size: 40px;
  line-height: 1.1;
  color: var(--color-title);
  margin: 0 0 var(--space-sm) 0;
}

.areas-intro {
  font-family: var(--font-body);
  font-size: 20px;
  line-height: 1.5;
  color: var(--color-text);
  margin: 0;
}

.areas-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-md);
}

.area-card {
  display: flex;
  flex-direction: column;
  background-color: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.area-card:hover {
  transform: translateY(-4px);
  box-shadow: 0px 14px 30px -12px rgba(82, 109, 130, 0.45);
}

.area-media {
  aspect-ratio: 4 / 3;
  background-color: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.area-media img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: var(--space-sm);
  box-sizing: border-box;
}

.area-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: var(--space-md);
}

.area-name {
  font-family: var(--font-subtitle);
  font-weight: 700;
  font-size: 24px;
  line-height: 1.2;
  color: var(--color-title);
  margin: 0 0 var(--space-xs) 0;
}

.area-promise {
  font-family: var(--font-body);
  font-size: 18px;
  line-height: 1.45;
  color: var(--color-text);
  margin: 0 0 var(--space-sm) 0;
}

.area-points {
  list-style: none;
  margin: 0 0 var(--space-md) 0;
  padding: 0;
  flex: 1;
}

.area-points li {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.4;
  color: var(--color-text);
  padding-left: 1.4rem;
  position: relative;
  margin-bottom: var(--space-xs);
}

.area-points li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.5em;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--color-accent);
}

.area-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  align-self: flex-start;
  font-family: var(--font-body);
  font-size: 17px;
  color: var(--color-action);
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: color 0.2s ease, border-color 0.2s ease, gap 0.2s ease;
}

.area-cta:hover {
  color: var(--color-brand);
  border-bottom-color: var(--color-brand);
  gap: 0.9rem;
}

@media (max-width: 1024px) {
  .areas-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 800px) {
  .areas-title { font-size: 1.75rem; text-align: center; margin-bottom: var(--space-md); }
  .areas-head { text-align: center; margin-left: auto; margin-right: auto; }
  .areas-grid { grid-template-columns: 1fr; }
}
</style>
